/* eslint-disable react/prop-types */
import { useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import SoftBox from "components/SoftBox";
import Button from "@mui/material/Button";
import SoftButton from "components/SoftButton";

// --- HELPERS ---
const createEquityObj = () => ({
  contributedCapital: "",
  sharePremium: "",
  bondConversionOption: "",
  otherCapital: "",
});

const createItem = () => ({
  recordingDate: "",
  documentNumber: "",
  documentDate: "",
  description: "",
  correspondingAccount: "",
  debit: createEquityObj(),
  credit: createEquityObj(),
  balance: createEquityObj(),
});

const defaultItems = [createItem(), createItem()];

function OwnerEquityDetailLedgerDetail({ formData }) {
  const { values = {}, onChangeValue } = formData || {};
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const today = new Date();

  // --- QUẢN LÝ STATE THÔNG TIN CHUNG ---
  const [headerInfo, setHeaderInfo] = useState({
    company: "",
    companyAddress: "",
    year: today.getFullYear().toString(),
    totalPages: "",
    openingDate: "",
    signingDay: today.getDate().toString(),
    signingMonth: (today.getMonth() + 1).toString(),
    signingYear: today.getFullYear().toString(),
    preparerName: "",
    chiefAccountantName: "",
    legalRepresentativeName: "",
  });

  // --- QUẢN LÝ STATE SỐ LIỆU ---
  const [openingBalance, setOpeningBalance] = useState(createEquityObj());
  const [localItems, setLocalItems] = useState(defaultItems);

  // Đồng bộ với formik values khi load API
  useEffect(() => {
    if (values.items && Array.isArray(values.items) && values.items.length > 0) {
      setLocalItems(values.items);
    }
    if (values.openingBalance) {
      setOpeningBalance({ ...createEquityObj(), ...values.openingBalance });
    }
    setHeaderInfo((prev) => ({ ...prev, ...values }));
  }, [values]);

  const handleHeaderChange = (field, val) => {
    setHeaderInfo((prev) => ({ ...prev, [field]: val }));
    if (onChangeValue) onChangeValue(field, val);
  };

  const num = (val) => (val && val !== "" && !isNaN(val) ? parseFloat(val) : 0);

  // Logic tính toán lại toàn bộ "Số dư" cho từng dòng (Dư = Dư trước + Có - Nợ)
  const recalculateBalances = (items, openBal) => {
    let currentBal = {
      contributedCapital: num(openBal.contributedCapital),
      sharePremium: num(openBal.sharePremium),
      bondConversionOption: num(openBal.bondConversionOption),
      otherCapital: num(openBal.otherCapital),
    };

    return items.map((item) => {
      currentBal = {
        contributedCapital:
          currentBal.contributedCapital +
          num(item.credit.contributedCapital) -
          num(item.debit.contributedCapital),
        sharePremium:
          currentBal.sharePremium + num(item.credit.sharePremium) - num(item.debit.sharePremium),
        bondConversionOption:
          currentBal.bondConversionOption +
          num(item.credit.bondConversionOption) -
          num(item.debit.bondConversionOption),
        otherCapital:
          currentBal.otherCapital + num(item.credit.otherCapital) - num(item.debit.otherCapital),
      };
      return { ...item, balance: { ...currentBal } };
    });
  };

  const handleOpeningBalanceChange = (field, val) => {
    const newOpenBal = { ...openingBalance, [field]: val };
    setOpeningBalance(newOpenBal);
    setLocalItems(recalculateBalances(localItems, newOpenBal));
  };

  const handleChangeItem = (index, mainField, subField, value) => {
    let newItems = [...localItems];
    if (subField) {
      newItems[index] = {
        ...newItems[index],
        [mainField]: { ...newItems[index][mainField], [subField]: value },
      };
    } else {
      newItems[index] = { ...newItems[index], [mainField]: value };
    }

    // Nếu thay đổi số liệu Nợ / Có -> Tính lại toàn bộ Số dư các dòng
    if (mainField === "debit" || mainField === "credit") {
      newItems = recalculateBalances(newItems, openingBalance);
    }
    setLocalItems(newItems);
  };

  const addRow = () => setLocalItems([...localItems, createItem()]);
  const removeRow = (index) => {
    if (localItems.length <= 1) return;
    const newItems = localItems.filter((_, i) => i !== index);
    setLocalItems(recalculateBalances(newItems, openingBalance));
  };

  // --- LOGIC TÍNH TỔNG CỘNG ---
  const calculatedTotals = useMemo(() => {
    const sum = (type, field) => localItems.reduce((acc, item) => acc + num(item[type][field]), 0);
    return {
      totalArisingDebit: {
        contributedCapital: sum("debit", "contributedCapital"),
        sharePremium: sum("debit", "sharePremium"),
        bondConversionOption: sum("debit", "bondConversionOption"),
        otherCapital: sum("debit", "otherCapital"),
      },
      totalArisingCredit: {
        contributedCapital: sum("credit", "contributedCapital"),
        sharePremium: sum("credit", "sharePremium"),
        bondConversionOption: sum("credit", "bondConversionOption"),
        otherCapital: sum("credit", "otherCapital"),
      },
    };
  }, [localItems]);

  const closingBalance = useMemo(() => {
    // Số dư cuối kỳ chính là số dư của dòng cuối cùng (nếu có), hoặc là số dư đầu kỳ
    if (localItems.length > 0) return localItems[localItems.length - 1].balance;
    return {
      contributedCapital: num(openingBalance.contributedCapital),
      sharePremium: num(openingBalance.sharePremium),
      bondConversionOption: num(openingBalance.bondConversionOption),
      otherCapital: num(openingBalance.otherCapital),
    };
  }, [localItems, openingBalance]);

  useEffect(() => {
    if (onChangeValue) {
      onChangeValue("items", localItems);
      onChangeValue("openingBalance", openingBalance);
      onChangeValue("totalArisingDebit", calculatedTotals.totalArisingDebit);
      onChangeValue("totalArisingCredit", calculatedTotals.totalArisingCredit);
      onChangeValue("closingBalance", closingBalance);
    }
  }, [localItems, openingBalance, calculatedTotals, closingBalance]);

  // --- STYLES ---
  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "12px",
    },
    headerTop: { display: "flex", justifyContent: "space-between", marginBottom: "15px" },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "12px",
      fontFamily: "inherit",
      backgroundColor: "transparent",
      display: "inline-block",
      color: "#000",
    },
    inputDottedSmall: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      width: "40px",
      fontSize: "12px",
      fontFamily: "inherit",
      textAlign: "center",
      backgroundColor: "transparent",
      display: "inline-block",
      color: "#000",
    },
    titleBox: { textAlign: "center", margin: "10px 0 20px 0" },
    titleMain: { fontSize: "16px", fontWeight: "bold", margin: 0, textTransform: "uppercase" },
    table: { width: "100%", borderCollapse: "collapse", fontSize: "11px", marginTop: "15px" },
    th: {
      border: "1px solid #000",
      padding: "4px",
      textAlign: "center",
      fontWeight: "bold",
      verticalAlign: "middle",
    },
    td: { border: "1px solid #000", padding: 0, height: "30px", verticalAlign: "middle" },
    tdText: { border: "1px solid #000", padding: "0 4px", height: "30px", verticalAlign: "middle" },
    inputTable: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "right",
      fontSize: "11px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 4px",
      display: "block",
      color: "#000",
    },
    inputTableCenter: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "center",
      fontSize: "11px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 2px",
      display: "block",
      color: "#000",
    },
    inputTableLeft: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "left",
      fontSize: "11px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 4px",
      display: "block",
      color: "#000",
    },
    totalText: {
      padding: "0 4px",
      textAlign: "right",
      fontWeight: "bold",
      display: "block",
      lineHeight: "30px",
    },
    rowBg: { backgroundColor: "#fafafa", fontWeight: "bold" },
    removeBtn: {
      border: "none",
      background: "transparent",
      color: "red",
      fontWeight: "bold",
      cursor: "pointer",
      width: "100%",
      height: "100%",
    },
  };

  const fmt = (n) => (n && n !== 0 ? new Intl.NumberFormat("vi-VN").format(n) : "");

  return (
    <SoftBox style={s.container}>
      {/* HEADER */}
      <div style={s.headerTop}>
        <div style={{ width: "40%" }}>
          <div style={{ fontWeight: "bold", marginBottom: 5 }}>
            Đơn vị:{" "}
            <input
              disabled={containView}
              style={{ ...s.inputDotted, width: "200px", fontWeight: "bold" }}
              value={headerInfo.company}
              onChange={(e) => handleHeaderChange("company", e.target.value)}
            />
          </div>
          <div style={{ fontWeight: "bold" }}>
            Địa chỉ:{" "}
            <input
              disabled={containView}
              style={{ ...s.inputDotted, width: "200px", fontWeight: "bold" }}
              value={headerInfo.companyAddress}
              onChange={(e) => handleHeaderChange("companyAddress", e.target.value)}
            />
          </div>
        </div>
        <div style={{ textAlign: "center", width: "60%" }}>
          <div style={{ fontWeight: "bold" }}>Mẫu số S51-DN</div>
          <div style={{ fontStyle: "italic", fontSize: "12px", marginTop: 4 }}>
            (Kèm theo Thông tư số 99/2025/TT-BTC <br /> ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ
            Tài chính)
          </div>
        </div>
      </div>

      {/* TITLE */}
      <div style={s.titleBox}>
        <h2 style={s.titleMain}>SỔ THEO DÕI CHI TIẾT VỐN ĐẦU TƯ CỦA CHỦ SỞ HỮU (TK 411)</h2>
        <div style={{ fontWeight: "bold", marginTop: 5, fontStyle: "italic" }}>
          Năm:{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDottedSmall, width: "60px", fontWeight: "bold" }}
            value={headerInfo.year}
            onChange={(e) => handleHeaderChange("year", e.target.value)}
          />
        </div>
      </div>

      {/* TABLE */}
      <div style={{ overflowX: "auto" }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th rowSpan={3} style={{ ...s.th, width: "50px" }}>
                Ngày
                <br />
                tháng
                <br />
                ghi sổ
              </th>
              <th colSpan={2} style={s.th}>
                Chứng từ
              </th>
              <th rowSpan={3} style={{ ...s.th, width: "120px" }}>
                Diễn
                <br />
                giải
              </th>
              <th rowSpan={3} style={{ ...s.th, width: "40px" }}>
                Tài
                <br />
                khoản
                <br />
                đối
                <br />
                ứng
              </th>
              <th colSpan={8} style={s.th}>
                Số phát sinh
              </th>
              <th colSpan={4} style={s.th}>
                Số dư
              </th>
              {!containView && (
                <th rowSpan={3} style={{ ...s.th, width: "30px" }}>
                  #
                </th>
              )}
            </tr>
            <tr>
              <th rowSpan={2} style={{ ...s.th, width: "50px" }}>
                Số
                <br />
                hiệu
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "50px" }}>
                Ngày
                <br />
                tháng
              </th>
              <th colSpan={4} style={s.th}>
                Nợ (giảm)
              </th>
              <th colSpan={4} style={s.th}>
                Có (tăng)
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "65px" }}>
                Vốn
                <br />
                góp
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "65px" }}>
                Thặng
                <br />
                dư vốn
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "65px" }}>
                Quyền
                <br />
                chọn
                <br />
                chuyển
                <br />
                đổi trái
                <br />
                phiếu
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "65px" }}>
                Vốn
                <br />
                khác
              </th>
            </tr>
            <tr>
              {/* Cột Nợ */}
              <th style={{ ...s.th, width: "65px" }}>
                Vốn
                <br />
                góp
              </th>
              <th style={{ ...s.th, width: "65px" }}>
                Thặng
                <br />
                dư vốn
              </th>
              <th style={{ ...s.th, width: "65px" }}>
                Quyền
                <br />
                chọn
                <br />
                chuyển
                <br />
                đổi trái
                <br />
                phiếu
              </th>
              <th style={{ ...s.th, width: "65px" }}>
                Vốn
                <br />
                khác
              </th>
              {/* Cột Có */}
              <th style={{ ...s.th, width: "65px" }}>
                Vốn
                <br />
                góp
              </th>
              <th style={{ ...s.th, width: "65px" }}>
                Thặng
                <br />
                dư vốn
              </th>
              <th style={{ ...s.th, width: "65px" }}>
                Quyền
                <br />
                chọn
                <br />
                chuyển
                <br />
                đổi trái
                <br />
                phiếu
              </th>
              <th style={{ ...s.th, width: "65px" }}>
                Vốn
                <br />
                khác
              </th>
            </tr>
            <tr style={{ backgroundColor: "#f0f0f0" }}>
              <th style={s.th}>A</th>
              <th style={s.th}>B</th>
              <th style={s.th}>C</th>
              <th style={s.th}>D</th>
              <th style={s.th}>E</th>
              <th style={s.th}>1</th>
              <th style={s.th}>2</th>
              <th style={s.th}>3</th>
              <th style={s.th}>4</th>
              <th style={s.th}>5</th>
              <th style={s.th}>6</th>
              <th style={s.th}>7</th>
              <th style={s.th}>8</th>
              <th style={s.th}>9</th>
              <th style={s.th}>10</th>
              <th style={s.th}>11</th>
              <th style={s.th}>12</th>
              {!containView && <th style={s.th}></th>}
            </tr>
          </thead>
          <tbody>
            {/* 1. SỐ DƯ ĐẦU KỲ */}
            <tr style={s.rowBg}>
              <td style={s.td} colSpan={3}></td>
              <td style={s.tdText}>- Số dư đầu kỳ</td>
              <td style={s.td} colSpan={9}></td>
              <td style={s.td}>
                <input
                  disabled={containView}
                  type="number"
                  style={{ ...s.inputTable, fontWeight: "bold" }}
                  value={openingBalance.contributedCapital}
                  onChange={(e) => handleOpeningBalanceChange("contributedCapital", e.target.value)}
                />
              </td>
              <td style={s.td}>
                <input
                  disabled={containView}
                  type="number"
                  style={{ ...s.inputTable, fontWeight: "bold" }}
                  value={openingBalance.sharePremium}
                  onChange={(e) => handleOpeningBalanceChange("sharePremium", e.target.value)}
                />
              </td>
              <td style={s.td}>
                <input
                  disabled={containView}
                  type="number"
                  style={{ ...s.inputTable, fontWeight: "bold" }}
                  value={openingBalance.bondConversionOption}
                  onChange={(e) =>
                    handleOpeningBalanceChange("bondConversionOption", e.target.value)
                  }
                />
              </td>
              <td style={s.td}>
                <input
                  disabled={containView}
                  type="number"
                  style={{ ...s.inputTable, fontWeight: "bold" }}
                  value={openingBalance.otherCapital}
                  onChange={(e) => handleOpeningBalanceChange("otherCapital", e.target.value)}
                />
              </td>
              {!containView && <td style={s.td}></td>}
            </tr>

            {/* LABEL PHÁT SINH */}
            <tr>
              <td style={s.td} colSpan={3}></td>
              <td style={s.tdText} className="bold">
                - Số phát sinh trong kỳ:
              </td>
              <td colSpan={13} style={s.td}></td>
              {!containView && <td style={s.td}></td>}
            </tr>

            {/* DÒNG PHÁT SINH (ITEMS) */}
            {localItems.map((row, i) => (
              <tr key={i}>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableCenter}
                    value={row.recordingDate || ""}
                    onChange={(e) => handleChangeItem(i, "recordingDate", null, e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableCenter}
                    value={row.documentNumber || ""}
                    onChange={(e) => handleChangeItem(i, "documentNumber", null, e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableCenter}
                    value={row.documentDate || ""}
                    onChange={(e) => handleChangeItem(i, "documentDate", null, e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableLeft}
                    value={row.description || ""}
                    onChange={(e) => handleChangeItem(i, "description", null, e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableCenter}
                    value={row.correspondingAccount || ""}
                    onChange={(e) =>
                      handleChangeItem(i, "correspondingAccount", null, e.target.value)
                    }
                  />
                </td>

                {/* Nợ (giảm) */}
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.debit.contributedCapital || ""}
                    onChange={(e) =>
                      handleChangeItem(i, "debit", "contributedCapital", e.target.value)
                    }
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.debit.sharePremium || ""}
                    onChange={(e) => handleChangeItem(i, "debit", "sharePremium", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.debit.bondConversionOption || ""}
                    onChange={(e) =>
                      handleChangeItem(i, "debit", "bondConversionOption", e.target.value)
                    }
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.debit.otherCapital || ""}
                    onChange={(e) => handleChangeItem(i, "debit", "otherCapital", e.target.value)}
                  />
                </td>

                {/* Có (tăng) */}
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.credit.contributedCapital || ""}
                    onChange={(e) =>
                      handleChangeItem(i, "credit", "contributedCapital", e.target.value)
                    }
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.credit.sharePremium || ""}
                    onChange={(e) => handleChangeItem(i, "credit", "sharePremium", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.credit.bondConversionOption || ""}
                    onChange={(e) =>
                      handleChangeItem(i, "credit", "bondConversionOption", e.target.value)
                    }
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.credit.otherCapital || ""}
                    onChange={(e) => handleChangeItem(i, "credit", "otherCapital", e.target.value)}
                  />
                </td>

                {/* Số dư (Auto Tính) */}
                <td style={s.td}>
                  <input
                    disabled={true}
                    type="number"
                    style={s.inputTable}
                    value={row.balance.contributedCapital || ""}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={true}
                    type="number"
                    style={s.inputTable}
                    value={row.balance.sharePremium || ""}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={true}
                    type="number"
                    style={s.inputTable}
                    value={row.balance.bondConversionOption || ""}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={true}
                    type="number"
                    style={s.inputTable}
                    value={row.balance.otherCapital || ""}
                  />
                </td>

                {!containView && (
                  <td style={{ ...s.td, textAlign: "center" }}>
                    <button type="button" style={s.removeBtn} onClick={() => removeRow(i)}>
                      X
                    </button>
                  </td>
                )}
              </tr>
            ))}

            {/* CỘNG SỐ PHÁT SINH */}
            <tr style={s.rowBg}>
              <td style={s.td} colSpan={3}></td>
              <td style={s.tdText}>- Cộng số phát sinh</td>
              <td style={s.tdText} className="text-center">
                x
              </td>

              {/* Nợ */}
              <td style={s.td}>
                <span style={s.totalText}>
                  {fmt(calculatedTotals.totalArisingDebit.contributedCapital)}
                </span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>
                  {fmt(calculatedTotals.totalArisingDebit.sharePremium)}
                </span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>
                  {fmt(calculatedTotals.totalArisingDebit.bondConversionOption)}
                </span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>
                  {fmt(calculatedTotals.totalArisingDebit.otherCapital)}
                </span>
              </td>

              {/* Có */}
              <td style={s.td}>
                <span style={s.totalText}>
                  {fmt(calculatedTotals.totalArisingCredit.contributedCapital)}
                </span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>
                  {fmt(calculatedTotals.totalArisingCredit.sharePremium)}
                </span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>
                  {fmt(calculatedTotals.totalArisingCredit.bondConversionOption)}
                </span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>
                  {fmt(calculatedTotals.totalArisingCredit.otherCapital)}
                </span>
              </td>

              <td colSpan={4} style={s.td}></td>
              {!containView && <td style={s.td}></td>}
            </tr>

            {/* SỐ DƯ CUỐI KỲ */}
            <tr style={s.rowBg}>
              <td style={s.td} colSpan={3}></td>
              <td style={s.tdText}>- Số dư cuối kỳ</td>
              <td colSpan={9} style={s.td}></td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(closingBalance.contributedCapital)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(closingBalance.sharePremium)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(closingBalance.bondConversionOption)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(closingBalance.otherCapital)}</span>
              </td>
              {!containView && <td style={s.td}></td>}
            </tr>
          </tbody>
        </table>
        {!containView && (
          <SoftBox mt={3} textAlign="center">
            <SoftButton variant="outlined" color="dark" onClick={addRow}>
              Thêm dòng
            </SoftButton>
            {/* <Button variant="outlined" color="dark" onClick={addRow} sx={{ mr: 2 }}>
                                 + Thêm dòng phát sinh
                               </Button> */}
          </SoftBox>
        )}
      </div>

      {/* FOOTER INFO */}
      <div style={{ marginTop: "20px", lineHeight: "1.8" }}>
        <div>
          - Sổ này có{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDottedSmall, width: "40px" }}
            value={headerInfo.totalPages}
            onChange={(e) => handleHeaderChange("totalPages", e.target.value)}
          />{" "}
          trang, đánh số từ trang 01 đến trang...
        </div>
        <div>
          - Ngày mở sổ:{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "150px" }}
            value={headerInfo.openingDate}
            onChange={(e) => handleHeaderChange("openingDate", e.target.value)}
          />
        </div>
      </div>

      {/* FOOTER CHỮ KÝ */}
      {/* <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "30px",
          textAlign: "center",
        }}
      >
        <div style={{ width: "33%" }}>
          <b>Người ghi sổ</b>
          <div style={{ fontStyle: "italic", fontSize: "12px", marginBottom: "65px" }}>
            (Ký, họ tên)
          </div>
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "150px", textAlign: "center" }}
            value={headerInfo.preparerName}
            onChange={(e) => handleHeaderChange("preparerName", e.target.value)}
          />
        </div>
        <div style={{ width: "33%" }}>
          <b>Kế toán trưởng</b>
          <div style={{ fontStyle: "italic", fontSize: "12px", marginBottom: "65px" }}>
            (Ký, họ tên)
          </div>
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "150px", textAlign: "center" }}
            value={headerInfo.chiefAccountantName}
            onChange={(e) => handleHeaderChange("chiefAccountantName", e.target.value)}
          />
        </div>
        <div style={{ width: "33%" }}>
          <div style={{ fontStyle: "italic", marginBottom: 5 }}>
            Ngày
            <input
              disabled={containView}
              style={{ ...s.inputDottedSmall, marginLeft: 4 }}
              value={headerInfo.signingDay}
              onChange={(e) => handleHeaderChange("signingDay", e.target.value)}
            />
            tháng
            <input
              disabled={containView}
              style={{ ...s.inputDottedSmall, marginLeft: 4 }}
              value={headerInfo.signingMonth}
              onChange={(e) => handleHeaderChange("signingMonth", e.target.value)}
            />
            năm
            <input
              disabled={containView}
              style={{ ...s.inputDottedSmall, width: "40px", marginLeft: 4 }}
              value={headerInfo.signingYear}
              onChange={(e) => handleHeaderChange("signingYear", e.target.value)}
            />
          </div>
          <b>Người đại diện theo pháp luật</b>
          <div style={{ fontStyle: "italic", fontSize: "12px", marginBottom: "45px" }}>
            (Ký, họ tên, đóng dấu)
          </div>
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "150px", textAlign: "center" }}
            value={headerInfo.legalRepresentativeName}
            onChange={(e) => handleHeaderChange("legalRepresentativeName", e.target.value)}
          />
        </div>
      </div> */}
    </SoftBox>
  );
}

OwnerEquityDetailLedgerDetail.propTypes = {
  formData: PropTypes.object,
};

export default OwnerEquityDetailLedgerDetail;
